import { Box, DialogContent, DialogTitle, IconButton, MenuItem } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { ReactComponent as CloseIcon } from "../../assets/icons/close-icon.svg";
import DialogComponentStyled from "../styled/dialog.component.styled";
import { MessageItem, IdText } from "@/models/apiModels";
import { MessageSourceDestinationType } from "@/models/enums";
import api from "@/services/api";
import { useTranslation } from "react-i18next";
import BaseInput from "@/components/common/baseInput";
import BaseDateInput from "@/components/common/baseDateInput";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import StyledSwitch from "@/components/common/baseSwitch";
import BaseButton from "@/components/common/baseButton";
import popupService from "@/services/popupService";

export interface IMessageDialogTitle {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

function MessageDialogTitle(props: IMessageDialogTitle) {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 0 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

interface ICreateEditMessageDialog {
    open: boolean;
    handleClose: () => void;
    handleSearch: () => void;
    messageId: number | null;
}

const СreateEditMessageDialog: FC<ICreateEditMessageDialog> = (props: ICreateEditMessageDialog) => {
    const { open, handleClose, handleSearch, messageId } = props;
    const minDate = dayjs().format("YYYY-MM-DD");
    const [message, setMessage] = useState<MessageItem>(new MessageItem());
    const { t } = useTranslation();

    useEffect(() => {
        getMessage(messageId);
    }, [messageId, open]);

    const newMessage = () => {
        const newMessage = new MessageItem();
        newMessage.validDateFrom = dayjs().format("YYYY-MM-DD");
        setMessage(newMessage);
    };

    const getMessage = async (id: number | null) => {
        newMessage();
        if (id) {
            const response = await api.getMessage(id);
            if (response.data) {
                setMessage(response.data);
            }
        }
    };

    const sourceDestinationTypeList: IdText[] = [
        new IdText("APP", t("messages.sourceDestination.APP")),
        new IdText("B2C", t("messages.sourceDestination.B2C")),
    ];

    function handleOnChangeStartDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setMessage((prevData) => ({
            ...prevData,
            validDateFrom: e ? formattedDate : null,
        }));
    }

    function handleOnChangeEndDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setMessage((prevData) => ({
            ...prevData,
            validDateTo: e ? formattedDate : null,
        }));
    }

    const handleOnChangeStatus = (newValue: boolean) => {
        setMessage((prevData) => ({
            ...prevData,
            active: newValue,
        }));
    };

    const submitOnClick = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        popupService.showLoading();
        try {
            await api.saveMessage(message);
            await popupService.showSuccess(t("messages.messageSaved"));
            handleClose();
            handleSearch();
        } catch (err) {
            popupService.showApiError(err);
            return;
        }
    };

    return (
        <Box>
            <DialogComponentStyled
                sx={{
                    zIndex: "1050",
                }}
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
            >
                <MessageDialogTitle id="customized-dialog-title" onClose={handleClose}>
                    {messageId ? (
                        <div className="message-dialog-title">
                            <i
                                style={{ marginInlineEnd: "10px" }}
                                className="icon-f icon-MessagesIndicatorIcon fs-16"
                            />
                            {t("messages.editMessage")}
                        </div>
                    ) : (
                        <div className="message-dialog-title">
                            <i
                                style={{ marginInlineEnd: "10px" }}
                                className="icon-f icon-MessagesIndicatorIcon fs-16"
                            />
                            {t("messages.createMessage")}
                        </div>
                    )}
                </MessageDialogTitle>
                <DialogContent
                    sx={{
                        minWidth: "453px",
                    }}
                    className="editMessageDialog"
                >
                    <form onSubmit={submitOnClick}>
                        <div>
                            <BaseInput
                                fullWidth
                                required
                                label={t("messages.title")}
                                size="small"
                                value={message?.title}
                                onChange={(event) =>
                                    setMessage((prevData) => ({
                                        ...prevData,
                                        title: event.target.value,
                                    }))
                                }
                                inputProps={{
                                    inputMode: "text",
                                    maxLength: 255,
                                }}
                            />
                        </div>
                        <div>
                            <BaseInput
                                select
                                required
                                fullWidth
                                label={t("message.messageDestinationType")}
                                size="small"
                                value={message.targetType}
                                onChange={(event) =>
                                    setMessage((prevData) => ({
                                        ...prevData,
                                        targetType: event.target.value,
                                    }))
                                }
                            >
                                {sourceDestinationTypeList.map((option) => (
                                    <MenuItem key={option.text} value={option.id}>
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </BaseInput>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                gap: 11,
                            }}
                        >
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopDatePicker
                                    label={t("message.validDateFrom")}
                                    inputFormat="DD/MM/YYYY"
                                    value={message.validDateFrom}
                                    minDate={minDate}
                                    onChange={(event) => handleOnChangeStartDate(event)}
                                    renderInput={(params) => (
                                        <BaseDateInput
                                            {...params}
                                            sx={{
                                                height: "40px",
                                                width: "221px",
                                            }}
                                            required
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopDatePicker
                                    label={t("message.validDateTo")}
                                    inputFormat="DD/MM/YYYY"
                                    value={message.validDateTo}
                                    minDate={message.validDateFrom}
                                    onChange={(event) => handleOnChangeEndDate(event)}
                                    renderInput={(params) => (
                                        <BaseDateInput
                                            {...params}
                                            sx={{
                                                height: "40px",
                                                width: "221px",
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </div>
                        <div className="create-employee-status font-style-16">
                            <span>{t("employee.NewEmpStatus")}</span>
                            <StyledSwitch
                                value={message.active}
                                checked={message.active}
                                onChange={() => handleOnChangeStatus(!message.active)}
                            />
                        </div>
                        <div>
                            <BaseInput
                                required
                                multiline
                                label={t("messages.content")}
                                type="text"
                                size="small"
                                fullWidth
                                sx={{
                                    height: 102,
                                }}
                                inputProps={{
                                    style: {
                                        height: "100%",
                                    },
                                    maxLength: 1000,
                                }}
                                value={message.content}
                                onChange={(event) =>
                                    setMessage((prevData) => ({
                                        ...prevData,
                                        content: event.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div className="edit-message-button-block">
                            <BaseButton
                                style={{ width: "50%", justifyContent: "center" }}
                                type="button"
                                onClick={handleClose}
                                className="cancel-button"
                            >
                                {t("button.cancel")}
                            </BaseButton>
                            <BaseButton
                                style={{ width: "50%", justifyContent: "center" }}
                                type="submit"
                                className="edit-save-button"
                            >
                                {t("button.save")}
                            </BaseButton>
                        </div>
                    </form>
                </DialogContent>
            </DialogComponentStyled>
        </Box>
    );
};

export default СreateEditMessageDialog;
